import type { Simplify } from '@eturi/ts-helpers'
import type { SharedAppState, WithFastPollState, WithRequestTrackingState } from '@op/app-shared'
import {
	fastPollSlice,
	fastPollSliceTransformer,
	requestTrackingSlice,
	requestTrackingSliceTransformer,
} from '@op/app-shared'
import type {
	WithAvatarState,
	WithBenefitsState,
	WithFeatureFlagsState,
	WithNotifState,
	WithOnboardingState,
	WithPrefsState,
	WithSampleStatsState,
} from '@op/services'
import {
	accessSlice,
	accessSliceTransformer,
	accountSlice,
	accountSliceTransformer,
	appsSlice,
	appsSliceTransformer,
	avatarSlice,
	avatarSliceTransformer,
	benefitsSlice,
	benefitsSliceTransformer,
	blockStatusSlice,
	blockStatusSliceTransformer,
	deviceSlice,
	deviceSliceTransformer,
	featureFlagsSlice,
	featureFlagsSliceTransformer,
	geoSlice,
	geoSliceTransformer,
	granularitySlice,
	granularitySliceTransformer,
	notificationSlice,
	notificationSliceTransformer,
	onboardingSlice,
	onboardingSliceTransformer,
	prefsSlice,
	purchaseSlice,
	purchaseSliceTransformer,
	ruleSlice,
	ruleSliceTransformer,
	sampleStatsSlice,
	userSlice,
	userSliceTransformer,
	vewConfigSlice,
	vewConfigSliceTransformer,
	vewDevicesSlice,
	vewDevicesSliceTransformer,
} from '@op/services'
import { combineReducers } from '@reduxjs/toolkit'
import { combineTransformers } from 'rtk-slice-transformer'
import type { WithAppMiscState } from './app-misc.slice'
import { appMiscSlice, appMiscSliceTransformer } from './app-misc.slice'
import type { WithBrowserInfoState } from './browser-info.slice'
import { browserInfoSlice, browserInfoSliceTransformer } from './browser-info.slice'
import type { WithChatState } from './chat.slice'
import { chatSlice, chatSliceTransformer } from './chat.slice'
import type { WithGalleryUIState } from './gallery-ui.slice'
import { galleryUISlice, galleryUISliceTransformer } from './gallery-ui.slice'
import type { WithGeoUIState } from './geo-ui.slice'
import { geoUISlice, geoUISliceTransformer } from './geo-ui.slice'
import type { WithHttpRetryState } from './http-retry.slice'
import { httpRetrySlice, httpRetrySliceTransformer } from './http-retry.slice'
import type { WithLandingState } from './landing.slice'
import { landingSlice, landingSliceTransformer } from './landing.slice'
import type { WithPairState } from './pair.slice'
import { pairSlice, pairSliceTransformer } from './pair.slice'
import type { WithProductState } from './product.slice'
import { productSlice, productSliceTransformer } from './product.slice'
import { samplesSlice, samplesSliceTransformer } from './samples.slice'

export type RootState = Simplify<
	SharedAppState &
		WithAppMiscState &
		WithAvatarState &
		WithBenefitsState &
		WithBrowserInfoState &
		WithChatState &
		WithFastPollState &
		WithFeatureFlagsState &
		WithGalleryUIState &
		WithOnboardingState &
		WithGeoUIState &
		WithHttpRetryState &
		WithLandingState &
		WithNotifState &
		WithPairState &
		WithPrefsState &
		WithProductState &
		WithRequestTrackingState &
		WithSampleStatsState
>

export const rootReducer = combineReducers({
	[accessSlice.name]: accessSlice.reducer,
	[accountSlice.name]: accountSlice.reducer,
	[appMiscSlice.name]: appMiscSlice.reducer,
	[appsSlice.name]: appsSlice.reducer,
	[avatarSlice.name]: avatarSlice.reducer,
	[benefitsSlice.name]: benefitsSlice.reducer,
	[blockStatusSlice.name]: blockStatusSlice.reducer,
	[browserInfoSlice.name]: browserInfoSlice.reducer,
	[chatSlice.name]: chatSlice.reducer,
	[deviceSlice.name]: deviceSlice.reducer,
	[fastPollSlice.name]: fastPollSlice.reducer,
	[featureFlagsSlice.name]: featureFlagsSlice.reducer,
	[galleryUISlice.name]: galleryUISlice.reducer,
	[geoSlice.name]: geoSlice.reducer,
	[geoUISlice.name]: geoUISlice.reducer,
	[granularitySlice.name]: granularitySlice.reducer,
	[httpRetrySlice.name]: httpRetrySlice.reducer,
	[landingSlice.name]: landingSlice.reducer,
	[notificationSlice.name]: notificationSlice.reducer,
	[onboardingSlice.name]: onboardingSlice.reducer,
	[pairSlice.name]: pairSlice.reducer,
	[prefsSlice.name]: prefsSlice.reducer,
	[productSlice.name]: productSlice.reducer,
	[purchaseSlice.name]: purchaseSlice.reducer,
	[requestTrackingSlice.name]: requestTrackingSlice.reducer,
	[ruleSlice.name]: ruleSlice.reducer,
	[samplesSlice.name]: samplesSlice.reducer,
	[sampleStatsSlice.name]: sampleStatsSlice.reducer,
	[userSlice.name]: userSlice.reducer,
	[vewConfigSlice.name]: vewConfigSlice.reducer,
	[vewDevicesSlice.name]: vewDevicesSlice.reducer,
})

export const sentryTransformer = combineTransformers([
	accessSliceTransformer,
	accountSliceTransformer,
	appMiscSliceTransformer,
	appsSliceTransformer,
	avatarSliceTransformer,
	benefitsSliceTransformer,
	blockStatusSliceTransformer,
	browserInfoSliceTransformer,
	chatSliceTransformer,
	deviceSliceTransformer,
	fastPollSliceTransformer,
	featureFlagsSliceTransformer,
	galleryUISliceTransformer,
	geoSliceTransformer,
	geoUISliceTransformer,
	granularitySliceTransformer,
	httpRetrySliceTransformer,
	landingSliceTransformer,
	notificationSliceTransformer,
	onboardingSliceTransformer,
	pairSliceTransformer,
	productSliceTransformer,
	purchaseSliceTransformer,
	requestTrackingSliceTransformer,
	ruleSliceTransformer,
	samplesSliceTransformer,
	userSliceTransformer,
	vewConfigSliceTransformer,
	vewDevicesSliceTransformer,
])
